/**
 * AnalyticsSummaryRow
 * Row of summary stat cards for analytics page
 */

import { Zap, Banknote, Clock, Activity, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/core/components/ui/card';
import { Skeleton } from '@/core/components/ui/skeleton';
import { AnalyticsSummary } from '../../types/dashboard-types';
import { useCurrencyFormatterRight } from '../../hooks/useCurrencyFormatter';
import { cn } from '@/core/lib/utils';

interface AnalyticsSummaryRowProps {
  summary: AnalyticsSummary | null;
  isLoading?: boolean;
}

interface SummaryCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  changePercent?: number;
  iconBgColor: string;
}

function SummaryCard({ icon, label, value, changePercent, iconBgColor }: SummaryCardProps) {
  const showChange = changePercent !== undefined;
  const isPositive = changePercent !== undefined && changePercent >= 0;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('p-2 rounded-lg', iconBgColor)}>
              {icon}
            </div>
            <div>
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-xl font-semibold text-gray-900 mt-0.5">{value}</p>
            </div>
          </div>
          {showChange && (
            <div
              className={cn(
                'flex items-center gap-1 text-sm font-medium',
                isPositive ? 'text-green-600' : 'text-red-600'
              )}
            >
              {isPositive ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              {Math.abs(changePercent).toFixed(1)}%
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SummaryCardSkeleton() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-24" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function AnalyticsSummaryRow({ summary, isLoading = false }: AnalyticsSummaryRowProps) {
  const formatCurrency = useCurrencyFormatterRight();

  if (isLoading || !summary) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SummaryCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${Math.round(minutes)} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <SummaryCard
        icon={<Banknote className="h-5 w-5 text-green-600" />}
        iconBgColor="bg-green-100"
        label="Total Revenue"
        value={formatCurrency(summary.totalRevenue)}
        changePercent={summary.revenueChangePercent}
      />
      <SummaryCard
        icon={<Zap className="h-5 w-5 text-blue-600" />}
        iconBgColor="bg-blue-100"
        label="Total Energy"
        value={`${summary.totalEnergy.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kWh`}
        changePercent={summary.energyChangePercent}
      />
      <SummaryCard
        icon={<Activity className="h-5 w-5 text-purple-600" />}
        iconBgColor="bg-purple-100"
        label="Total Sessions"
        value={summary.totalSessions.toLocaleString()}
      />
      <SummaryCard
        icon={<Clock className="h-5 w-5 text-purple-600" />}
        iconBgColor="bg-purple-100"
        label="Avg Session Duration"
        value={formatDuration(summary.averageSessionDuration)}
      />
    </div>
  );
}
